import { useEffect, useRef } from 'react';
import styles from '@components/Modal/Modal.styles';
import Button from '@components/Button/Button';
import Heading from '@components/Heading/Heading';
import Icon from '@components/icons/Icon';
import useBodyOverflow from '@hooks/useBodyOverflow';
type PropsType = {
  children: React.ReactNode;
  closeFunc: (active: boolean) => void;
  active: boolean;
  heading: string;
  closeOnOutsideClick?: boolean;
  overlayAddSx?: Record<string, unknown>;
  addSx?: Record<string, unknown>;
  addContentSx?: Record<string, unknown>;
  variant?: 'default' | 'login';
  fullScreen?: boolean;
  headingAddSx?: Record<string, unknown>;
  borderLess?: boolean;
  pdfLayout?: boolean;
};
const Modal = ({
  children,
  closeFunc,
  active,
  heading,
  closeOnOutsideClick = false,
  overlayAddSx = {},
  addSx = {},
  addContentSx = {},
  variant = 'default',
  fullScreen = false,
  headingAddSx = {},
  borderLess = false,
  pdfLayout = false
}: PropsType) => {
  useBodyOverflow(active, 'modal');
  const modalRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const isLoginVariant = variant === 'login';

  // Focus trap - WCAG 2.1.2 No Keyboard Trap (Level A)
  useEffect(() => {
    if (!active || !modalRef.current) return;

    // Save the element that had focus before opening modal
    previousActiveElementRef.current = document.activeElement as HTMLElement;

    // Get all focusable elements in the modal
    const getFocusableElements = (): HTMLElement[] => {
      if (!modalRef.current) return [];
      const focusableSelectors = ['button:not([disabled])', 'a[href]', 'input:not([disabled])', 'select:not([disabled])', 'textarea:not([disabled])', '[tabindex]:not([tabindex="-1"])', '[role="button"]:not([aria-disabled="true"])', '[role="link"]', '[role="menuitem"]', '[role="tab"]'].join(', ');
      const elements = Array.from(modalRef.current.querySelectorAll<HTMLElement>(focusableSelectors));

      // Filter out elements that are not visible or not focusable
      return elements.filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && !el.hasAttribute('disabled') && el.tabIndex !== -1;
      });
    };

    // Focus the first focusable element
    const focusableElements = getFocusableElements();
    if (focusableElements.length > 0) {
      // Small delay to ensure modal is fully rendered
      setTimeout(() => {
        focusableElements[0]?.focus();
      }, 100);
    }

    // Handle keyboard navigation
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeFunc(false);
        // Restore focus to the element that opened the modal
        if (previousActiveElementRef.current) {
          previousActiveElementRef.current.focus();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const focusableElements = getFocusableElements();
      if (focusableElements.length === 0) return;
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);
      if (e.shiftKey) {
        // Shift + Tab - navigate backwards
        if (currentIndex === 0 || document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab - navigate forwards
        if (currentIndex === focusableElements.length - 1 || document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    // Handle clicks outside modal
    const checkIfClickedOutside = (e: MouseEvent) => {
      const element = e.target as HTMLElement;

      // Sprawdź czy kliknięty element nie jest interaktywnym elementem (przycisk, link itp.)
      const isInteractiveElement = element.closest('button, a, [role="button"], input, select, textarea, [tabindex="0"]');
      if (!modalRef.current?.contains(element) && closeOnOutsideClick && !isInteractiveElement) {
        closeFunc(false);
        // Restore focus to the element that opened the modal
        if (previousActiveElementRef.current) {
          previousActiveElementRef.current.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    if (closeOnOutsideClick) {
      // Dodać małe opóźnienie żeby uniknąć zamknięcia po kliknięciu w przycisk otwierający modał
      const timer = setTimeout(() => {
        window.addEventListener('click', checkIfClickedOutside);
      }, 100);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('click', checkIfClickedOutside);
        document.removeEventListener('keydown', handleKeyDown);
        // Restore focus when modal closes
        if (previousActiveElementRef.current && document.activeElement === document.body) {
          previousActiveElementRef.current.focus();
        }
      };
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus when modal closes
      if (previousActiveElementRef.current && document.activeElement === document.body) {
        previousActiveElementRef.current.focus();
      }
    };
  }, [active, closeOnOutsideClick, closeFunc]);
  return <div sx={styles.areaModal(active, overlayAddSx, fullScreen, borderLess, pdfLayout)} data-testid="modal-area">
      <div sx={styles.modal(addSx, fullScreen, borderLess, pdfLayout)} ref={modalRef} data-testid="modal-dialog">
        <div sx={styles.modalHeading(isLoginVariant, fullScreen, pdfLayout)} data-testid="modal-heading">
          <div sx={isLoginVariant ? styles.headingWrapper : undefined} data-testid="modal-heading-wrapper">
            <Heading h={4} headingType={pdfLayout ? undefined : isLoginVariant ? 1 : 4} addSx={pdfLayout ? {
            ...styles.pdfHeadingTitle,
            ...headingAddSx
          } : headingAddSx} mobile={!pdfLayout} align={pdfLayout ? 'left' : 'center'} data-testid="modal-heading-title">
              {heading}
            </Heading>
          </div>
          <div sx={pdfLayout ? styles.pdfCloseButton : isLoginVariant ? styles.closeButtonWrapper : undefined} data-testid="modal-close-button-wrapper">
            <Button variant="iconOnly" onClick={() => closeFunc(false)} cy="modal-close-button">
              <Icon icon="Close3" size="30px" cy="modal-close-icon" />
            </Button>
          </div>
        </div>
        <div sx={styles.modalContent(addContentSx, pdfLayout)} data-testid="modal-content">
          {children}
        </div>
      </div>
    </div>;
};
export default Modal;